/**
 * Days, business days, and the instant a day ends somewhere.
 *
 * An anchor date (a start date, a departure date) is a *day*, not an instant:
 * HR types "the 14th", and the 14th is the 14th wherever the server is. So
 * days travel as `YYYY-MM-DD` strings and all the arithmetic here is done on
 * UTC midnights, where there is no daylight saving to step over. Only at the
 * very end -- "when is this task actually overdue?" -- does a day become an
 * instant, and that needs the site's time zone (`endOfDay`).
 *
 * The PRD (section 6.2) makes business days the default unit, with which days
 * count and which holidays are skipped set through the hierarchy. A `Calendar`
 * is the resolved answer to both questions for one site.
 */

/** `YYYY-MM-DD`. */
export type IsoDate = string;

/** 0 is Sunday, 6 is Saturday, as `Date.getUTCDay()` counts them. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface Calendar {
  /** Which days of the week are working days. Monday to Friday unless the hierarchy says otherwise. */
  workdays: readonly Weekday[];
  /** Days that are never working days, whatever weekday they fall on. */
  holidays: readonly IsoDate[];
}

export const MONDAY_TO_FRIDAY: readonly Weekday[] = [1, 2, 3, 4, 5];

const DAY_MS = 86_400_000;
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar day written as `YYYY-MM-DD`; "2026-02-30" is not one. */
export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string") return false;
  const match = ISO.exec(value);
  if (!match) return false;
  const [, y, m, d] = match;
  const ms = Date.UTC(Number(y), Number(m) - 1, Number(d));
  return toIso(ms) === value;
}

function toMs(date: IsoDate): number {
  const match = ISO.exec(date);
  if (!match) throw new RangeError(`Not a date: ${date}`);
  const [, y, m, d] = match;
  return Date.UTC(Number(y), Number(m) - 1, Number(d));
}

function toIso(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10);
}

export function weekday(date: IsoDate): Weekday {
  return new Date(toMs(date)).getUTCDay() as Weekday;
}

export function addCalendarDays(date: IsoDate, days: number): IsoDate {
  return toIso(toMs(date) + days * DAY_MS);
}

export function isBusinessDay(date: IsoDate, calendar: Calendar): boolean {
  return calendar.workdays.includes(weekday(date)) && !calendar.holidays.includes(date);
}

/**
 * The day `days` business days after (or, negative, before) `date`.
 *
 * Zero is the date itself, even when that is a weekend: an anchor date is a
 * day somebody chose, and "Day 0" means that day. Every other offset lands on
 * a business day, counting only business days on the way, so Day -1 before a
 * Monday start is the Friday before it.
 *
 * A calendar with no working days at all would never arrive anywhere, so it is
 * refused rather than looped on.
 */
export function addBusinessDays(date: IsoDate, days: number, calendar: Calendar): IsoDate {
  if (!Number.isInteger(days)) throw new RangeError(`Business days must be whole: ${days}`);
  if (days === 0) return date;
  if (calendar.workdays.length === 0)
    throw new RangeError("A calendar with no working days has no business days.");

  const step = days > 0 ? 1 : -1;
  let remaining = Math.abs(days);
  let cursor = date;
  while (remaining > 0) {
    cursor = addCalendarDays(cursor, step);
    if (isBusinessDay(cursor, calendar)) remaining -= 1;
  }
  return cursor;
}

/**
 * How many business days from `from` until `to`: positive when `to` is later.
 * Counts the days stepped onto, so Friday to the following Monday is 1.
 */
export function businessDaysBetween(from: IsoDate, to: IsoDate, calendar: Calendar): number {
  const start = toMs(from);
  const end = toMs(to);
  if (start === end) return 0;
  const step = end > start ? 1 : -1;
  let count = 0;
  for (let ms = start + step * DAY_MS; step > 0 ? ms <= end : ms >= end; ms += step * DAY_MS) {
    if (isBusinessDay(toIso(ms), calendar)) count += step;
  }
  return count;
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The offset, in milliseconds, of `timeZone` from UTC at `instant`.
 * `Intl` is the only thing that knows the rules, and it exists in every
 * runtime this package runs in (browser, workerd, node).
 */
function zoneOffset(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return asUtc - (instant - (instant % 1000));
}

/**
 * The last millisecond of `date` on a wall clock in `timeZone`, as epoch ms.
 *
 * A task due on the 14th is overdue the moment the 14th is over *at the site*,
 * not at UTC midnight -- which for a site in California would be 5pm. Checked
 * twice because the offset at a first guess can differ from the offset at the
 * answer when a daylight-saving change falls in between.
 */
export function endOfDay(date: IsoDate, timeZone: string): number {
  return fromWall(toMs(date) + DAY_MS - 1, timeZone);
}

/** A wall-clock reading in `timeZone`, written as if it were UTC, as the instant it names. */
function fromWall(wall: number, timeZone: string): number {
  const guess = wall - zoneOffset(wall, timeZone);
  return wall - zoneOffset(guess, timeZone);
}

/** `HH:MM` on a 24-hour clock: a time of day with no day and no zone. */
export type ClockTime = string;

export function isClockTime(value: unknown): value is ClockTime {
  return typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/**
 * `time` on `date` on a wall clock in `timeZone`, as epoch ms -- "5pm on
 * their last day, at the site". A time the clocks skip in spring lands an
 * hour later, as a person setting an alarm for it would find.
 */
export function atTime(date: IsoDate, time: ClockTime, timeZone: string): number {
  const [hours, minutes] = time.split(":").map(Number) as [number, number];
  return fromWall(toMs(date) + (hours * 60 + minutes) * 60_000, timeZone);
}

/** The calendar day it is at `instant` in `timeZone`. */
export function dayAt(instant: number, timeZone: string): IsoDate {
  return toIso(instant + zoneOffset(instant, timeZone));
}

/** True when `timeZone` is an IANA zone this runtime knows. */
export function isTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || timeZone === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}
